import type { PointMm } from '../schema/primitives.js';

export interface BBoxMm {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function bboxOfPoints(points: readonly PointMm[]): BBoxMm {
  if (points.length === 0) throw new Error('bbox de una lista vacia de puntos');
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function bboxWidth(b: BBoxMm): number {
  return b.maxX - b.minX;
}
export function bboxHeight(b: BBoxMm): number {
  return b.maxY - b.minY;
}

export function bboxCorners(b: BBoxMm): PointMm[] {
  return [
    { x: b.minX, y: b.minY },
    { x: b.maxX, y: b.minY },
    { x: b.maxX, y: b.maxY },
    { x: b.minX, y: b.maxY },
  ];
}

export function bboxUnion(a: BBoxMm, b: BBoxMm): BBoxMm {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** Punto dentro de poligono (ray casting). El borde cuenta como dentro. */
export function pointInPolygon(p: PointMm, polygon: readonly PointMm[], epsilon = 1e-9): boolean {
  const n = polygon.length;
  if (n < 3) return false;

  // Borde: si el punto esta sobre un segmento, esta dentro.
  for (let i = 0, j = n - 1; i < n; j = i++) {
    if (pointOnSegment(p, polygon[j]!, polygon[i]!, epsilon)) return true;
  }

  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const pi = polygon[i]!;
    const pj = polygon[j]!;
    const intersects = pi.y > p.y !== pj.y > p.y && p.x < ((pj.x - pi.x) * (p.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function pointOnSegment(p: PointMm, a: PointMm, b: PointMm, epsilon = 1e-9): boolean {
  const cross = (p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x);
  if (Math.abs(cross) > epsilon) return false;
  const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
  if (dot < -epsilon) return false;
  const lenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  return dot <= lenSq + epsilon;
}

function orientation(a: PointMm, b: PointMm, c: PointMm): number {
  const v = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
  if (Math.abs(v) < 1e-12) return 0;
  return v > 0 ? 1 : 2;
}

export function segmentsIntersect(p1: PointMm, q1: PointMm, p2: PointMm, q2: PointMm): boolean {
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && pointOnSegment(p2, p1, q1)) return true;
  if (o2 === 0 && pointOnSegment(q2, p1, q1)) return true;
  if (o3 === 0 && pointOnSegment(p1, p2, q2)) return true;
  if (o4 === 0 && pointOnSegment(q1, p2, q2)) return true;
  return false;
}

/**
 * Todos los puntos de `inner` estan dentro de `outer` Y ninguna arista los
 * cruza. Comprobar solo los vertices seria falso para un poligono concavo:
 * dos vertices dentro pueden tener el segmento que los une por fuera.
 */
export function polygonContainsPolygon(outer: readonly PointMm[], inner: readonly PointMm[]): boolean {
  if (inner.length === 0) return true;
  for (const p of inner) if (!pointInPolygon(p, outer)) return false;

  for (let i = 0; i < inner.length; i++) {
    const a = inner[i]!;
    const b = inner[(i + 1) % inner.length]!;
    for (let j = 0; j < outer.length; j++) {
      const c = outer[j]!;
      const d = outer[(j + 1) % outer.length]!;
      if (segmentsIntersect(a, b, c, d)) {
        // Tocar el borde no es salirse: solo cuenta si el punto medio queda fuera.
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (!pointInPolygon(mid, outer)) return false;
      }
    }
  }
  return true;
}

/** Distancia con signo de un punto al borde del poligono: >0 dentro, <0 fuera. */
export function signedDistanceToPolygon(p: PointMm, polygon: readonly PointMm[]): number {
  let min = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % polygon.length]!;
    min = Math.min(min, distanceToSegment(p, a, b));
  }
  return pointInPolygon(p, polygon) ? min : -min;
}

export function distanceToSegment(p: PointMm, a: PointMm, b: PointMm): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function polygonArea(polygon: readonly PointMm[]): number {
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % polygon.length]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/**
 * GIRA UN POLIGONO sobre su propio centro.
 *
 * La zona de marcaje viene del patron con una orientacion, y no siempre es la
 * que hace falta: un bolsillo puede admitir un estampado apaisado, y una
 * espalda puede querer el arte en vertical. Hasta ahora eso obligaba a
 * reescribir el poligono a mano.
 *
 * Se gira sobre el CENTRO de la propia zona y se devuelve trasladado al origen,
 * porque una zona no vive en un sitio del mundo: vive donde el patron dice, y
 * lo unico que cambia aqui es su forma.
 */
export function rotarPoligonoMm(puntos: readonly PointMm[], grados: number): PointMm[] {
  if (puntos.length === 0) return [];

  const xs = puntos.map((p) => p.x);
  const ys = puntos.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const cx = (minX + Math.max(...xs)) / 2;
  const cy = (minY + Math.max(...ys)) / 2;

  const radianes = (grados * Math.PI) / 180;
  const cos = Math.cos(radianes);
  const sen = Math.sin(radianes);

  const girados = puntos.map((p) => ({
    x: cx + (p.x - cx) * cos - (p.y - cy) * sen,
    y: cy + (p.x - cx) * sen + (p.y - cy) * cos,
  }));

  /*
    Se devuelve pegado a la esquina de donde estaba.

    Girar 90 grados un rectangulo de 280x400 lo deja de 400x280, y sin
    recolocarlo empezaria en coordenadas negativas: el documento canonico
    admite numeros negativos, pero una zona que empieza en -60 mm no significa
    nada para quien la mire.
  */
  const gx = Math.min(...girados.map((p) => p.x));
  const gy = Math.min(...girados.map((p) => p.y));
  return girados.map((p) => ({
    x: Number((p.x - gx + minX).toFixed(3)),
    y: Number((p.y - gy + minY).toFixed(3)),
  }));
}
